/**
 * Hand-written wire types of the REST API (docs/rest-api-migration.md, section 5). They mirror the backend DTO
 * records one to one.
 *
 * Conventions on the wire:
 *  - JSON, camelCase, `null` is sent explicitly (Jackson default inclusion).
 *  - Money is a decimal string (`"12.30"`), timestamps are ISO-8601 strings (`"2026-09-01T10:00:00Z"`).
 *  - Shopify ids are the numeric tail of the GID, as a string (`"8123"` for `gid://shopify/Product/8123`).
 */

/** Decimal string, e.g. `"12.30"`. Feed it to `eur()` from `@/tools/money`. */
export type Money = string;

/** ISO-8601 offset date-time, e.g. `"2026-09-01T10:00:00Z"`. */
export type IsoDateTime = string;

// ---------------------------------------------------------------------------------------------------------------------
// Errors and paging
// ---------------------------------------------------------------------------------------------------------------------

/**
 * RFC 9457 problem detail returned by `ApiExceptionHandler`. `code` is the optional machine-readable discriminator:
 * `NO_PARTNER`, `FORBIDDEN`, `NOT_FOUND`, `VALIDATION_FAILED`, `PARTNER_NAME_TAKEN`, `USER_EMAIL_TAKEN`,
 * `FULFILLMENT_FAILED`, ...
 *
 * `status` is always filled in by the client from the HTTP status, so it is present even when the body is not
 * a problem detail (a 401 from the security entry point has no body). Read it with `problemOf(error)` from `@/api`.
 */
export interface ProblemDetail {
    status: number;
    detail?: string;
    code?: string;
    type?: string;
    title?: string;
    instance?: string;
}

/** DB pagination: `?page=0&size=10`. `page` is zero-based, `size` is clamped to 100 by the server. */
export interface Page<T> {
    items: T[];
    page: number;
    size: number;
    total: number;
}

/** Shopify cursor pagination: `?first=10&after=<endCursor>`. `first` is clamped to 250 by the server. */
export interface CursorPage<T> {
    items: T[];
    endCursor: string | null;
    hasNextPage: boolean;
}

// ---------------------------------------------------------------------------------------------------------------------
// Session and users
// ---------------------------------------------------------------------------------------------------------------------

/** `GET /api/me`. A non-admin with `partner: null` still gets 200 (the "account not set up" overlay). */
export interface MeDto {
    id: number;
    name: string;
    email: string;
    isAdmin: boolean;
    profilePictureUrl: string | null;
    partner: PartnerDto | null;
}

export interface PartnerRefDto {
    id: number;
    name: string;
}

export interface UserDto {
    id: number;
    name: string;
    email: string;
    isAdmin: boolean;
    partner: PartnerRefDto | null;
}

/** `POST /api/users`. The email is trimmed and lower-cased by the server; a duplicate is 409 `USER_EMAIL_TAKEN`. */
export interface CreateUserInput {
    name: string;
    email: string;
    isAdmin: boolean;
    partnerId?: number | null;
}

/** `PUT /api/users/{id}`. Omitting `partnerId` unassigns the partner. */
export interface UpdateUserInput {
    isAdmin: boolean;
    partnerId?: number | null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------------------------------------------------

export interface DeliveryTime {
    min: number | null;
    max: number | null;
}

export interface PartnerDetails {
    description: string | null;
    registryCode: string | null;
    vatCode: string | null;
    representative: string | null;
    iban: string | null;
    phoneNr: string | null;
    homePage: string | null;
    logoUrl: string | null;
    dateJoined: string | null;
}

export interface PartnerDto {
    id: number;
    name: string;
    email: string;
    externalName: string;
    partnerCharge: number;
    additionalEmails: string[];
    itemSpecificCharge: boolean;
    globalDeliveryTime: DeliveryTime;
    partnerDetails: PartnerDetails;
}

/**
 * Body of `POST /api/partners`, `PUT /api/partners/{id}` and `PUT /api/me/partner`: a `PartnerDto` without `id`.
 * `name`, `email` and `externalName` are required and `partnerCharge >= 0`. `PUT /api/me/partner` ignores
 * `partnerCharge` and `name`, and `PUT /api/partners/{id}` ignores changes to `name` (the Shopify vendor key).
 */
export type PartnerInput = Omit<PartnerDto, "id">;

/** Row of the Partners table (`GET /api/partners`). */
export interface PartnerSummaryDto {
    id: number;
    name: string;
    email: string;
    partnerCharge: number;
}

/** Entry of the partner select (`GET /api/partners/options`), sorted by `externalName`. */
export interface PartnerOptionDto {
    id: number;
    name: string;
    externalName: string;
}

/** `GET /api/partners/{id}/order-counts`: `months` has 12 entries, January first. */
export interface PartnerOrderCountsDto {
    year: number;
    months: number[];
}

// ---------------------------------------------------------------------------------------------------------------------
// Orders and partner orders
// ---------------------------------------------------------------------------------------------------------------------

/** Values of the backend enum `PartnerOrderStatus`. */
export enum PartnerOrderStatus {
    Cancelled = "CANCELLED",
    EmailSent = "EMAIL_SENT",
    EmailSkipped = "EMAIL_SKIPPED",
    Error = "ERROR",
    New = "NEW",
    OutOfStock = "OUT_OF_STOCK",
    PackageArrived = "PACKAGE_ARRIVED",
    PackagePosted = "PACKAGE_POSTED",
    PackageReceived = "PACKAGE_RECEIVED",
    Processing = "PROCESSING",
    Regenerate = "REGENERATE"
}

/** Values of the backend enum `ShippingMethod`. */
export type ShippingMethod = "SMARTPOST";

/** Row of the admin order list (`GET /api/orders`). */
export interface OrderDto {
    name: string;
    /** The column is nullable. */
    creationTime: IsoDateTime | null;
    /** The column is nullable. */
    customerName: string | null;
    totalAmount: Money;
    /** Status of the single partner order; `null` when the order has 0 or 2+ partner orders. */
    status: PartnerOrderStatus | null;
    withdrawalRequested: boolean;
}

export interface LineItemDto {
    name: string;
    quantity: number;
    currentQuantity: number;
    /** `originalUnitPriceSet.fragments.shopMoneyBag.shopMoney.amount`, flattened. */
    unitPrice: Money;
}

export interface PartnerOrderPartnerDto {
    name: string;
    externalName: string;
}

export interface PartnerOrderDto {
    /** Addresses the row in `resend` and `fulfill`. */
    id: number;
    orderName: string;
    /** The column is nullable. */
    orderCreationTime: IsoDateTime | null;
    partner: PartnerOrderPartnerDto;
    status: PartnerOrderStatus;
    shippingMethod: ShippingMethod | null;
    barcode: string | null;
    hasLabel: boolean;
    /** Computed only when the list was requested with `orderName`; `null` otherwise. */
    canFulfill: boolean | null;
    /** Only present with `include=lineItems`. */
    lineItems?: LineItemDto[];
}

/** `POST /api/partner-orders/{id}/resend` */
export interface ResendPartnerOrderInput {
    newLabel: boolean;
}

/** `POST /api/partner-orders/{id}/fulfill` */
export interface FulfillPartnerOrderInput {
    notifyCustomer: boolean;
    barcode?: string | null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Products and collections
// ---------------------------------------------------------------------------------------------------------------------

/** Sort keys accepted by `GET /api/products`. */
export enum ProductSortKeys {
    Title = "TITLE",
    Vendor = "VENDOR"
}

export interface ProductSummaryDto {
    id: string;
    title: string;
    vendor: string;
    featuredImageUrl: string | null;
    /** Id of the product this review copy was made from. */
    reviewOf: string | null;
}

export interface ProductVariantInventoryDto {
    id: string;
    title: string;
    deliveryTime: string | null;
    tracked: boolean;
    /** Sum of the inventory level quantities; `null` when the variant has no inventory level. */
    available: number | null;
}

export interface ProductInventoryDto {
    id: string;
    title: string;
    variants: ProductVariantInventoryDto[];
}

/** `PUT /api/product-variants/{variantId}/inventory`. `available` is required when `tracked` is true and ignored otherwise. */
export interface ProductInventoryInput {
    tracked: boolean;
    available?: number | null;
    /** `null` or `""` leaves the stored value unchanged. */
    deliveryTime?: string | null;
}

export interface CollectionDto {
    id: number;
    name: string;
    tracked: boolean;
    inventory: number;
    deliveryTime: string;
}

/** `PUT /api/collections/{id}/inventory` */
export interface CollectionInventoryInput {
    value: number;
    tracked: boolean;
    /** `null` or missing keeps the stored value; an empty string overwrites it (unlike product variants, where `""` is ignored). */
    deliveryTime?: string | null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------------------------------------------------

/** `GET /api/admin/settings` and the body and response of `PUT /api/admin/settings`. */
export interface AdminSettingsDto {
    sendEmailsToPartners: boolean;
    fulfillOrders: boolean;
    sendReminderEmails: boolean;
    sendNewPartnerEmails: boolean;
    deliveryEnabledLt: boolean;
    deliveryEnabledLv: boolean;
    deliveryEnabledFi: boolean;
}

/** `GET /api/admin/webhook` and the body and response of `PUT /api/admin/webhook`. */
export interface WebhookDto {
    enabled: boolean;
}

/** `GET /api/admin/jobs/next-fire-time`. `nextFireTime` is `null` for a paused, completed or unknown job. */
export interface NextFireTimeDto {
    name: string;
    nextFireTime: IsoDateTime | null;
}

/** Values of the backend enum `EventLog.EventType`. */
export type EventType =
    | "PARTNER_CREATED"
    | "PARTNER_EDITED"
    | "INVENTORY_COUNT_UPDATED"
    | "USER_EDITED"
    | "FULFILLMENT_ERROR"
    | "VENDOR_ORDER_ERROR"
    | "VENDOR_ORDER_SENT"
    | "VENDOR_ORDER_SKIPPED"
    | "SUMMARY_ERROR"
    | "SUMMARY_SENT"
    | "SUMMARY_SKIPPED"
    | "PRODUCT_INVENTORY_EDITED"
    | "PRODUCT_REVIEW_CREATED"
    | "PRODUCT_EDITED"
    | "PRODUCT_REVIEW_APPROVED"
    | "ORDER_FULFILLED"
    | "COLLECTION_EDITED"
    | "USER_CREATED";

export interface EventDto {
    id: number;
    eventType: EventType;
    eventTime: IsoDateTime;
    /** Nullable column in the database, the spec example only shows a string. */
    content: string | null;
    userName: string | null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------------------------------------------------

/** `POST /api/feedback` */
export interface FeedbackInput {
    feedback: string;
}
